import { useMemo } from 'react'
import {
  RadialBarChart,
  RadialBar,
  LineChart,
  ComposedChart, // 追加
  Line,
  Area,          // 追加
  Scatter,       // 追加
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  PolarAngleAxis,
} from 'recharts'
import { format } from 'date-fns'
import clsx from 'clsx'
import { formatPriceWithSymbol } from '../../types'
import type { PredictionResult, EnsembleResult } from '../../types'
import { getSignal, SIGNAL_TEXT_COLOR } from '../../utils/signal'

interface Props {
  prediction: PredictionResult
  currentPrice: number
  symbol: string
  ensemble?: EnsembleResult // オプションで統合データをサポート
}

// ── Probability Gauge ──────────────────────────────────────────────────────

function ProbabilityGauge({ value }: { value: number }) {
  const pct = Math.round(value * 100)
  const isUp = value > 0.65
  const isDown = value < 0.35
  const fillColor = isUp ? '#00d4aa' : isDown ? '#ff4d6d' : '#f59e0b'
  const label = isUp ? '強気' : isDown ? '弱気' : '中立'
  const labelColor = isUp ? 'text-trading-green' : isDown ? 'text-trading-red' : 'text-trading-yellow'

  const gaugeData = [
    { name: 'rise', value: pct, fill: fillColor },
  ]

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: 160, height: 100 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            cx="50%"
            cy="100%"
            innerRadius={60}
            outerRadius={90}
            startAngle={180}
            endAngle={0}
            data={gaugeData}
          >
            {/* Background track */}
            <RadialBar
              dataKey="value"
              cornerRadius={6}
              background={{ fill: '#1f2937' }}
              isAnimationActive={true}
            />
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          </RadialBarChart>
        </ResponsiveContainer>
        {/* Center label */}
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
          <span className={clsx('text-2xl font-bold font-mono tabular-nums', labelColor)}>
            {pct}%
          </span>
        </div>
      </div>
      <span className={clsx('text-xs font-bold tracking-widest uppercase mt-1', labelColor)}>
        {label}
      </span>
      <span className="text-[10px] text-trading-muted mt-0.5">上昇確率</span>
    </div>
  )
}

// ── Monte Carlo Paths ──────────────────────────────────────────────────────

interface PathChartProps {
  prediction: PredictionResult
  currentPrice: number
  symbol: string
}

function PathChart({ prediction, currentPrice, symbol }: PathChartProps) {
  const data = useMemo(() => {
    const { timestamps, median, samples } = prediction
    const samplesSubset = samples.slice(0, 10)

    const stepMs = timestamps.length >= 2
      ? timestamps[1] - timestamps[0]
      : 86_400_000
    const nowTs = Date.now()

    const nowRow: Record<string, number> = {
      timestamp: nowTs,
      median: currentPrice,
      p10: currentPrice,
      p90: currentPrice,
    }
    samplesSubset.forEach((_, si) => { nowRow[`s${si}`] = currentPrice })

    const futureRows = timestamps.map((_, i) => {
      const row: Record<string, number> = {
        timestamp: nowTs + (i + 1) * stepMs,
        median: median[i],
        p10: prediction.p10[i],
        p90: prediction.p90[i],
      }
      samplesSubset.forEach((path, si) => {
        row[`s${si}`] = path[i] ?? median[i]
      })
      return row
    })

    return [nowRow, ...futureRows]
  }, [prediction, currentPrice])

  if (data.length === 0) return null

  const allVals = data.flatMap((d) => {
    const nums: number[] = [d.median, d.p10, d.p90]
    for (let i = 0; i < 10; i++) {
      if (d[`s${i}`] !== undefined) nums.push(d[`s${i}`] as number)
    }
    return nums
  })
  const yMin = Math.min(...allVals)
  const yMax = Math.max(...allVals)
  const pad = (yMax - yMin) * 0.05

  function formatPrice(v: number) {
    return formatPriceWithSymbol(v, symbol)
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
        <XAxis
          dataKey="timestamp"
          tickFormatter={(ts) => format(new Date(ts), 'MM/dd HH:mm')}
          tick={{ fontSize: 10, fill: '#64748b', fontFamily: 'monospace' }}
          tickLine={false}
          axisLine={{ stroke: '#1f2937' }}
          interval="preserveStartEnd"
          minTickGap={50}
        />
        <YAxis
          domain={[yMin - pad, yMax + pad]}
          tickFormatter={formatPrice}
          tick={{ fontSize: 10, fill: '#64748b', fontFamily: 'monospace' }}
          tickLine={false}
          axisLine={false}
          width={68}
          orientation="right"
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null
            return (
              <div className="bg-trading-panel border border-trading-border rounded-lg p-2 text-xs font-mono shadow-xl">
                <div className="text-trading-muted mb-1">
                  {format(new Date(label as number), 'MMM dd HH:mm')}
                </div>
                <div className="text-orange-400">
                  Median: {formatPrice(payload.find((p) => p.dataKey === 'median')?.value as number ?? 0)}
                </div>
              </div>
            )
          }}
        />
        {Array.from({ length: 10 }, (_, i) => (
          <Line
            key={`s${i}`}
            type="monotone"
            dataKey={`s${i}`}
            stroke="rgba(148,163,184,0.18)"
            strokeWidth={1}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        ))}
        <Line
          type="monotone"
          dataKey="median"
          stroke="#f97316"
          strokeWidth={2.5}
          strokeDasharray="6 3"
          dot={false}
          isAnimationActive={false}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

// ── Ensemble Path Chart ────────────────────────────────────────────────────

interface EnsemblePathChartProps {
  ensemble: EnsembleResult
  currentPrice: number
  symbol: string
}

function EnsemblePathChart({ ensemble, currentPrice, symbol }: EnsemblePathChartProps) {
  const data = useMemo(() => {
    const { timestamps, ensemble_median, long_cloud, samples, signals } = ensemble
    const samplesSubset = samples.slice(0, 10)
    
    const stepMs = timestamps.length >= 2
      ? timestamps[1] - timestamps[0]
      : 3600000
    const nowTs = Date.now()

    // Compose composite rows for Recharts Area/Scatter/Line
    const nowRow: Record<string, any> = {
      timestamp: nowTs,
      median: currentPrice,
      cloud10_90: [currentPrice, currentPrice],
      cloud25_75: [currentPrice, currentPrice],
    }
    samplesSubset.forEach((_, si) => { nowRow[`s${si}`] = currentPrice })

    const futureRows = timestamps.map((_, i) => {
      const ts = nowTs + (i + 1) * stepMs

      // Actually simplified approach: signals just mapped to a property
      const sig = signals.find(s => s.timestamp === timestamps[i])
      
      const row: Record<string, any> = {
        timestamp: ts,
        median: ensemble_median[i],
        cloud10_90: [long_cloud.p10[i], long_cloud.p90[i]],
        cloud25_75: [long_cloud.p25[i], long_cloud.p75[i]],
        signalBuy: sig?.type === 'BUY' ? ensemble_median[i] : null,
        signalSell: sig?.type === 'SELL' ? ensemble_median[i] : null,
      }
      samplesSubset.forEach((path, si) => {
        row[`s${si}`] = path[i] ?? ensemble_median[i]
      })
      return row
    })

    return [nowRow, ...futureRows]
  }, [ensemble, currentPrice])

  if (data.length === 0) return null

  const allVals = data.flatMap((d) => {
    const base = [d.median as number]
    if (d.cloud10_90) base.push(d.cloud10_90[0], d.cloud10_90[1])
    return base
  })
  const yMin = Math.min(...allVals)
  const yMax = Math.max(...allVals)
  const pad = (yMax - yMin) * 0.05

  function formatPrice(v: number) {
    return formatPriceWithSymbol(v, symbol)
  }

  return (
    <ResponsiveContainer width="100%" height={240}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
        <XAxis
          dataKey="timestamp"
          tickFormatter={(ts) => format(new Date(ts), 'MM/dd HH:mm')}
          tick={{ fontSize: 10, fill: '#64748b', fontFamily: 'monospace' }}
          tickLine={false}
          axisLine={{ stroke: '#1f2937' }}
          interval="preserveStartEnd"
          minTickGap={50}
        />
        <YAxis
          domain={[yMin - pad, yMax + pad]}
          tickFormatter={formatPrice}
          tick={{ fontSize: 10, fill: '#64748b', fontFamily: 'monospace' }}
          tickLine={false}
          axisLine={false}
          width={68}
          orientation="right"
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null
            return (
              <div className="bg-trading-panel border border-trading-border rounded-lg p-2 text-xs font-mono shadow-xl">
                <div className="text-trading-muted mb-1">
                  {format(new Date(label as number), 'MM/dd HH:mm')}
                </div>
                <div className="text-cyan-400 font-bold">
                  Ensemble Med: {formatPrice(payload.find((p) => p.dataKey === 'median')?.value as number ?? 0)}
                </div>
                <div className="text-trading-muted/70 mt-1 text-[10px]">
                  Background: 1D Trend Cloud
                </div>
              </div>
            )
          }}
        />
        
        {/* Cloud Backgrounds (Long-term Range) */}
        <Area 
          type="monotone" 
          dataKey="cloud10_90" 
          fill="#334155" 
          stroke="none" 
          fillOpacity={0.15} 
          isAnimationActive={false} 
          connectNulls
        />
        <Area 
          type="monotone" 
          dataKey="cloud25_75" 
          fill="#475569" 
          stroke="none" 
          fillOpacity={0.25} 
          isAnimationActive={false}
          connectNulls
        />

        {/* Short samples paths */}
        {Array.from({ length: 10 }, (_, i) => (
          <Line
            key={`s${i}`}
            type="monotone"
            dataKey={`s${i}`}
            stroke="rgba(148,163,184,0.15)"
            strokeWidth={1}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        ))}

        {/* Ensemble Line - Bold glowing cyan/neon */}
        <Line
          type="monotone"
          dataKey="median"
          stroke="#06b6d4" 
          strokeWidth={3}
          dot={false}
          isAnimationActive={false}
          connectNulls
        />

        {/* Buy / Sell Signals */}
        <Scatter 
          dataKey="signalBuy" 
          fill="#00d4aa" 
          shape="triangle"
          isAnimationActive={false}
        />
        <Scatter 
          dataKey="signalSell" 
          fill="#ff4d6d" 
          shape="triangle"
          transform="rotate(180)" // This won't strictly work directly like this, we usually use custom shape or Scatter renders dots.
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

// ── Volatility Bar ─────────────────────────────────────────────────────────

function VolatilityBar({
  predicted,
  label,
}: {
  predicted: number
  label: string
}) {
  const pct = Math.min(predicted * 100, 20) // cap at 20% for display
  const level = predicted > 0.03 ? 'HIGH' : predicted > 0.015 ? 'MED' : 'LOW'
  const barColor =
    level === 'HIGH' ? 'bg-trading-red'
    : level === 'MED' ? 'bg-trading-yellow'
    : 'bg-trading-green'

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-trading-muted uppercase tracking-wider">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-mono font-semibold tabular-nums text-trading-text">
            {(predicted * 100).toFixed(2)}%
          </span>
          <span
            className={clsx(
              'text-[9px] font-bold px-1.5 py-0.5 rounded',
              level === 'HIGH' ? 'bg-trading-red/20 text-trading-red'
              : level === 'MED' ? 'bg-trading-yellow/20 text-trading-yellow'
              : 'bg-trading-green/20 text-trading-green',
            )}
          >
            {level === 'HIGH' ? '高' : level === 'MED' ? '中' : '低'}
          </span>
        </div>
      </div>
      <div className="w-full h-2 bg-trading-bg rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all duration-700', barColor)}
          style={{ width: `${(pct / 20) * 100}%` }}
        />
      </div>
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function PredictionChart({ prediction, currentPrice, symbol, ensemble }: Props) {
  const riseProb = ensemble ? ensemble.rise_probability : prediction.rise_probability
  const predVol = ensemble ? ensemble.predicted_volatility : prediction.predicted_volatility
  const durationValue = 24
  const unit = '時間'

  return (
    <div className="flex flex-col gap-4">
      {/* Top stats row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="trading-card text-center">
          <div className="stat-label mb-1">上昇確率 ({ensemble ? '統合' : '単独'})</div>
          <div className={clsx('text-2xl font-bold font-mono tabular-nums', SIGNAL_TEXT_COLOR[getSignal(riseProb)])}>
            {Math.round(riseProb * 100)}%
          </div>
        </div>
        <div className="trading-card text-center">
          <div className="stat-label mb-1">予測ボラティリティ</div>
          <div className="text-2xl font-bold font-mono tabular-nums text-trading-yellow">
            {(predVol * 100).toFixed(2)}%
          </div>
        </div>
        <div className="trading-card text-center">
          <div className="stat-label mb-1">予測期間</div>
          <div className="text-2xl font-bold font-mono tabular-nums text-trading-blue">
            {ensemble ? '24時間(統合)' : `${durationValue}${unit}`}
          </div>
        </div>
      </div>

      {/* Gauge + Volatility */}
      <div className="grid grid-cols-2 gap-4">
        {/* Probability gauge */}
        <div className="trading-card flex flex-col items-center justify-center gap-3 py-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-trading-muted">
            AI シグナル {ensemble && '(アンサンブル)'}
          </h3>
          <ProbabilityGauge value={riseProb} />
          <div className="text-[10px] text-trading-muted text-center">
            {symbol} · {ensemble ? 'マルチタイムフレーム' : `${durationValue}${unit}`} 予測
          </div>
        </div>

        {/* Volatility + summary */}
        <div className="trading-card flex flex-col gap-4 justify-center">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-trading-muted">
            ボラティリティ予測
          </h3>
          <VolatilityBar
            predicted={predVol}
            label="予測ボラティリティ"
          />
          <div className="divider" />
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="stat-label">現在価格</div>
              <div className="font-mono font-semibold text-trading-text tabular-nums mt-0.5">
                {formatPriceWithSymbol(currentPrice, symbol)}
              </div>
            </div>
            <div>
              <div className="stat-label">サンプル数</div>
              <div className="font-mono font-semibold text-trading-text tabular-nums mt-0.5">
                {prediction.samples.length}
              </div>
            </div>
            <div>
              <div className="stat-label">P10 ターゲット</div>
              <div className="font-mono font-semibold text-trading-red tabular-nums mt-0.5">
                {formatPriceWithSymbol(prediction.p10[prediction.p10.length - 1], symbol)}
              </div>
            </div>
            <div>
              <div className="stat-label">P90 ターゲット</div>
              <div className="font-mono font-semibold text-trading-green tabular-nums mt-0.5">
                {formatPriceWithSymbol(prediction.p90[prediction.p90.length - 1], symbol)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monte Carlo paths */}
      <div className="chart-container">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-trading-muted mb-4 flex items-center justify-between">
          <div>
            {ensemble ? '統合マルチタイムフレーム予測' : 'モンテカルロ・シミュレーション経路'}
            <span className="ml-2 text-[10px] text-trading-muted/60 normal-case font-normal">
              ({ensemble ? '1Dクラウド + 1H経路' : `${prediction.samples.length}本中10本の経路を表示`})
            </span>
          </div>
          {ensemble && (
             <div className="flex items-center gap-3 text-[9px] font-medium">
               <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#06b6d4]"></div><span>アンサンブル</span></div>
               <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#475569] opacity-50"></div><span>長期帯</span></div>
             </div>
          )}
        </h3>
        {ensemble ? (
          <EnsemblePathChart ensemble={ensemble} currentPrice={currentPrice} symbol={symbol} />
        ) : (
          <PathChart prediction={prediction} currentPrice={currentPrice} symbol={symbol} />
        )}
      </div>
    </div>
  )
}
